/**
 * Admin · Coupon Checkout Verifications
 *
 * COUPON-ELIGIBILITY-1 · Phase A — Admin review surface.
 *
 * Route: /admin/checkout-verifications
 *
 * Queue of storefront checkout submissions with three actions per row:
 *   • Verify            → status VERIFIED (records reviewer + verified_url)
 *   • Not Supported     → status NOT_SUPPORTED (reason required)
 *   • Request More Info → status back to PENDING_REVIEW + merchant-safe note
 *
 * Off-domain checkout warning is surfaced but NEVER auto-rejects — human
 * decides per rule §off-domain.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  ShieldCheck, ShieldAlert, MessagesSquare, ExternalLink, RefreshCw,
  Filter, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  PENDING_REVIEW: { label: "Pending review",
                    chip: "bg-sky-50 text-sky-800 ring-sky-200" },
  VERIFIED:       { label: "Verified",
                    chip: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  NOT_SUPPORTED:  { label: "Not supported",
                    chip: "bg-rose-50 text-rose-800 ring-rose-200" },
  NOT_CHECKED:    { label: "Not checked",
                    chip: "bg-amber-50 text-amber-800 ring-amber-200" },
  GRANDFATHERED:  { label: "Grandfathered",
                    chip: "bg-slate-100 text-slate-700 ring-slate-200" },
};

function domainOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return ""; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }); } catch { return iso; }
}

export default function AdminCheckoutVerifications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("PENDING_REVIEW");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { row, decision, reason, verified_url }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "ALL" ? {} : { status: filter };
      const { data } = await api.get(
        "/admin/coupon-checkout-verifications",
        { params: { ...params, limit: 200 } }
      );
      setItems(data?.items || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to load verification queue"
      );
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openDecision = (row, decision) => {
    setModal({
      row,
      decision,
      reason: "",
      verified_url: row.submitted_url || "",
    });
  };

  const decide = async (e) => {
    e.preventDefault();
    if (!modal) return;
    const { row, decision, reason, verified_url } = modal;
    if ((decision === "NOT_SUPPORTED" || decision === "NEEDS_INFO")
        && !reason.trim()) {
      toast.error("Reviewer note is required for this decision");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        decision,
        verification_reason: reason.trim() || null,
      };
      if (decision === "VERIFIED") payload.verified_url = verified_url.trim();
      await api.post(
        `/admin/coupon-checkout-verifications/${row.storefront_id}/decide`,
        payload
      );
      toast.success(
        decision === "VERIFIED" ? "Marked verified" :
        decision === "NOT_SUPPORTED" ? "Marked not supported" :
        "Sent back to merchant with note"
      );
      setModal(null);
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.message ||
        e?.response?.data?.detail || "Decision failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const filters = useMemo(() => [
    ["PENDING_REVIEW", "Pending"],
    ["VERIFIED", "Verified"],
    ["NOT_SUPPORTED", "Not supported"],
    ["ALL", "All"],
  ], []);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6"
         data-testid="admin-checkout-verifications-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Coupon eligibility · Phase A
          </div>
          <h1 className="font-display text-3xl text-slate-900">
            Checkout Verifications
          </h1>
          <p className="text-sm text-slate-600 max-w-3xl mt-1">
            Confirm each merchant's checkout has a visible coupon-entry
            field before enabling Coupon Code offers.
          </p>
        </div>
        <button type="button" onClick={load}
                className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="cvq-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-slate-500" />
        {filters.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                    filter === v ? "pf-btn-primary" : "pf-btn-glass"
                  }`}
                  data-testid={`cvq-filter-${v}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="pf-card divide-y divide-slate-200/60">
        {loading && (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2"
               data-testid="cvq-loading">
            <RefreshCw className="w-4 h-4 animate-spin" /> Loading queue…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-8 text-sm text-slate-500 text-center"
               data-testid="cvq-empty">
            No submissions match this filter.
          </div>
        )}
        {!loading && items.map((row) => {
          const status = row.status;
          const meta = STATUS_LABELS[status] || STATUS_LABELS.PENDING_REVIEW;
          const submittedDomain = domainOf(row.submitted_url);
          const websiteDomain = domainOf(row.storefront_website_url);
          const offDomain = !!(submittedDomain && websiteDomain
                                && submittedDomain !== websiteDomain
                                && !submittedDomain.endsWith("." + websiteDomain)
                                && !websiteDomain.endsWith("." + submittedDomain));
          return (
            <div key={row.id} className="p-5 space-y-3"
                 data-testid={`cvq-row-${row.storefront_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px] space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg text-slate-900">
                      {row.storefront_name || row.storefront_slug || row.storefront_id}
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${meta.chip}`}
                          data-testid={`cvq-status-${row.storefront_id}`}>
                      {meta.label}
                    </span>
                    {offDomain && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2 py-0.5 text-[11px] font-semibold"
                            data-testid={`cvq-off-domain-${row.storefront_id}`}>
                        <AlertTriangle className="w-3 h-3" />
                        Off-domain checkout — review manually
                      </span>
                    )}
                  </div>
                  {row.storefront_website_url && (
                    <div className="text-xs text-slate-500">
                      Merchant site:{" "}
                      <a href={row.storefront_website_url} target="_blank"
                         rel="noopener noreferrer" className="hover:underline">
                        {row.storefront_website_url}
                      </a>
                    </div>
                  )}
                  {row.submitted_url && (
                    <div className="text-xs text-slate-700">
                      <span className="font-semibold">Submitted checkout:</span>{" "}
                      <a href={row.submitted_url} target="_blank"
                         rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 hover:underline break-all"
                         data-testid={`cvq-submitted-url-${row.storefront_id}`}>
                        {row.submitted_url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                  {row.merchant_notes && (
                    <div className="text-xs bg-slate-50 rounded-xl px-3 py-2 ring-1 ring-slate-200 max-w-2xl">
                      <div className="font-semibold text-slate-600 uppercase tracking-widest text-[10px]">
                        Merchant instructions
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">
                        {row.merchant_notes}
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-1">
                    Submitted {fmtDate(row.submitted_at)}
                    {row.reviewed_at && (
                      <> · Reviewed {fmtDate(row.reviewed_at)}</>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0 min-w-[160px]">
                  <button type="button"
                          onClick={() => openDecision(row, "VERIFIED")}
                          className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid={`cvq-verify-${row.storefront_id}`}>
                    <ShieldCheck className="w-3.5 h-3.5" /> Verify
                  </button>
                  <button type="button"
                          onClick={() => openDecision(row, "NEEDS_INFO")}
                          className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid={`cvq-needs-info-${row.storefront_id}`}>
                    <MessagesSquare className="w-3.5 h-3.5" /> Request more info
                  </button>
                  <button type="button"
                          onClick={() => openDecision(row, "NOT_SUPPORTED")}
                          className="pf-btn-glass text-rose-700 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid={`cvq-not-supported-${row.storefront_id}`}>
                    <ShieldAlert className="w-3.5 h-3.5" /> Not supported
                  </button>
                </div>
              </div>
              {row.verification_reason && (
                <div className="text-xs text-slate-600 bg-slate-50 rounded-xl px-3 py-2 ring-1 ring-slate-200 max-w-3xl">
                  <span className="font-semibold">Last reviewer note:</span>{" "}
                  {row.verification_reason}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => !busy && setModal(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={decide}
                className="glass-strong rounded-3xl w-full max-w-lg p-6"
                data-testid="cvq-decision-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-slate-900">
                {modal.decision === "VERIFIED" && "Mark as Verified"}
                {modal.decision === "NOT_SUPPORTED" && "Mark as Not Supported"}
                {modal.decision === "NEEDS_INFO" && "Request more information"}
              </h3>
              <button type="button" onClick={() => !busy && setModal(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs text-slate-500 mb-3">
              Storefront:{" "}
              <span className="font-semibold text-slate-700">
                {modal.row.storefront_name}
              </span>
            </div>

            {modal.decision === "VERIFIED" && (
              <>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Verified checkout URL
                </label>
                <input type="url" required
                       value={modal.verified_url}
                       onChange={(e) => setModal({ ...modal, verified_url: e.target.value })}
                       className="pf-input w-full px-3 py-2 mt-1 text-sm font-mono"
                       data-testid="cvq-verified-url" />
              </>
            )}

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">
              {modal.decision === "NOT_SUPPORTED" && "Reason (shown to merchant) *"}
              {modal.decision === "NEEDS_INFO" && "Merchant-safe request (shown to merchant) *"}
              {modal.decision === "VERIFIED" && "Reviewer note (optional)"}
            </label>
            <textarea rows={4} value={modal.reason}
                      onChange={(e) => setModal({ ...modal, reason: e.target.value })}
                      className="pf-input w-full px-3 py-2 mt-1 text-sm"
                      placeholder={
                        modal.decision === "NEEDS_INFO"
                          ? "e.g. Please share the cart URL after adding an item — we can't see the promo field on the current URL."
                          : modal.decision === "NOT_SUPPORTED"
                          ? "e.g. Checkout page has no coupon field and Auto-Discount isn't wired up."
                          : "Optional: 'Confirmed via cart flow'"
                      }
                      maxLength={2000}
                      data-testid="cvq-reason" />
            <div className="text-[11px] text-slate-500 mt-1">
              This text is shown to the merchant — keep it merchant-safe. Do
              NOT include internal admin notes.
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => !busy && setModal(null)}
                      className="pf-btn-glass px-4 py-2 rounded-full text-sm">
                Cancel
              </button>
              <button type="submit" disabled={busy}
                      className={`px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-60 ${
                        modal.decision === "VERIFIED"
                          ? "pf-btn-primary"
                          : "pf-btn-glass"
                      }`}
                      data-testid="cvq-decide-submit">
                {busy ? "Saving…" : "Confirm"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
